// Renders an equirectangular 360 video onto the inside of a virtual sphere and lets
// the user drag to look around. Ported from the WPF Avalias.VideoPlayer360 control.
//
// Rotation (0..1): 0.5 looks at the centre of the video; 0 and 1 both look directly behind.
// Tilt (0..1): 0.5 looks at the horizon; 0 looks straight down; 1 looks straight up.

const VERTEX_SHADER = `
attribute vec2 aPosition;
varying vec2 vPosition;
void main() {
    vPosition = aPosition;
    gl_Position = vec4(aPosition, 0.0, 1.0);
}`;

// Casts a ray from the camera (at the centre of the sphere) through each pixel and
// samples the equirectangular video texture at that ray's longitude/latitude.
const FRAGMENT_SHADER = `
precision highp float;
varying vec2 vPosition;
uniform sampler2D uVideo;
uniform vec2 uTanHalfFov;
uniform float uYaw;
uniform float uPitch;
const float PI = 3.14159265358979;
void main() {
    float x = vPosition.x * uTanHalfFov.x;
    float y = vPosition.y * uTanHalfFov.y;
    float f = 1.0;

    float cp = cos(uPitch), sp = sin(uPitch);
    float y1 = y * cp + f * sp;
    float f1 = f * cp - y * sp;

    float cy = cos(uYaw), sy = sin(uYaw);
    float x2 = x * cy + f1 * sy;
    float f2 = f1 * cy - x * sy;

    vec3 dir = normalize(vec3(x2, y1, f2));
    float lon = atan(dir.x, dir.z);
    float lat = asin(clamp(dir.y, -1.0, 1.0));
    vec2 uv = vec2(0.5 + lon / (2.0 * PI), 0.5 - lat / PI);
    gl_FragColor = texture2D(uVideo, uv);
}`;

// Matches the WPF camera's 100 degree horizontal field of view.
const HORIZONTAL_FOV_DEGREES = 100;
// Prevents looking fully up or down, where the view would flip orientation.
const TILT_LIMIT = 0.0001;
// A full-width drag rotates the view by half a turn, as in the WPF control.
const DRAG_SENSITIVITY_X = 2;
const DRAG_SENSITIVITY_Y = 2;

const wrap = (x: number) => x - Math.floor(x);
const clip = (x: number, min = 0, max = 1) => Math.min(Math.max(min, x), max);

class VideoPlayer360 {
    rotation = 0.5;
    tilt = 0.5;

    private gl: WebGLRenderingContext;
    private program: WebGLProgram;
    private texture: WebGLTexture;
    private uTanHalfFov: WebGLUniformLocation;
    private uYaw: WebGLUniformLocation;
    private uPitch: WebGLUniformLocation;
    private animationFrame = 0;
    private lastVideoTime = -1;
    private hasFrame = false;
    private dragPointerId: number | null = null;
    private lastDragX = 0;
    private lastDragY = 0;
    private dragStartX = 0;
    private dragStartY = 0;
    private suppressClick = false;
    private disposed = false;

    constructor(private canvas: HTMLCanvasElement, private video: HTMLVideoElement) {
        const gl = canvas.getContext("webgl", { antialias: false, premultipliedAlpha: false });
        if (!gl) throw new Error("WebGL is not supported, so 360 video cannot be displayed.");
        this.gl = gl;
        this.program = this.createProgram();
        this.texture = this.createTexture();
        this.uTanHalfFov = gl.getUniformLocation(this.program, "uTanHalfFov");
        this.uYaw = gl.getUniformLocation(this.program, "uYaw");
        this.uPitch = gl.getUniformLocation(this.program, "uPitch");

        canvas.style.touchAction = "none";
        canvas.addEventListener("pointerdown", this.onPointerDown);
        canvas.addEventListener("pointermove", this.onPointerMove);
        canvas.addEventListener("pointerup", this.onPointerUp);
        canvas.addEventListener("pointercancel", this.onPointerUp);
        canvas.addEventListener("click", this.onClick, true);

        this.animationFrame = requestAnimationFrame(this.render);
    }

    dispose() {
        this.disposed = true;
        cancelAnimationFrame(this.animationFrame);
        this.canvas.removeEventListener("pointerdown", this.onPointerDown);
        this.canvas.removeEventListener("pointermove", this.onPointerMove);
        this.canvas.removeEventListener("pointerup", this.onPointerUp);
        this.canvas.removeEventListener("pointercancel", this.onPointerUp);
        this.canvas.removeEventListener("click", this.onClick, true);
        this.gl.deleteTexture(this.texture);
        this.gl.deleteProgram(this.program);
    }

    resetView() {
        this.rotation = 0.5;
        this.tilt = 0.5;
    }

    private createShader(type: number, source: string) {
        const gl = this.gl;
        const shader = gl.createShader(type);
        gl.shaderSource(shader, source);
        gl.compileShader(shader);
        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
            throw new Error(`Unable to compile 360 video shader: ${gl.getShaderInfoLog(shader)}`);
        }
        return shader;
    }

    private createProgram() {
        const gl = this.gl;
        const program = gl.createProgram();
        gl.attachShader(program, this.createShader(gl.VERTEX_SHADER, VERTEX_SHADER));
        gl.attachShader(program, this.createShader(gl.FRAGMENT_SHADER, FRAGMENT_SHADER));
        gl.linkProgram(program);
        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
            throw new Error(`Unable to link 360 video shader: ${gl.getProgramInfoLog(program)}`);
        }
        gl.useProgram(program);

        const buffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
        const aPosition = gl.getAttribLocation(program, "aPosition");
        gl.enableVertexAttribArray(aPosition);
        gl.vertexAttribPointer(aPosition, 2, gl.FLOAT, false, 0, 0);
        return program;
    }

    private createTexture() {
        const gl = this.gl;
        const texture = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, texture);
        // Video textures are usually not power-of-two sized, so no mipmaps and clamp to edge.
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 255]));
        return texture;
    }

    private resizeCanvas() {
        const dpr = window.devicePixelRatio || 1;
        const width = Math.max(1, Math.round(this.canvas.clientWidth * dpr));
        const height = Math.max(1, Math.round(this.canvas.clientHeight * dpr));
        if (this.canvas.width !== width || this.canvas.height !== height) {
            this.canvas.width = width;
            this.canvas.height = height;
        }
    }

    private uploadVideoFrame() {
        const video = this.video;
        if (video.readyState < video.HAVE_CURRENT_DATA) return;
        if (this.hasFrame && video.currentTime === this.lastVideoTime) return;
        try {
            const gl = this.gl;
            gl.bindTexture(gl.TEXTURE_2D, this.texture);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);
            this.lastVideoTime = video.currentTime;
            this.hasFrame = true;
        } catch (err) {
            console.warn("Unable to upload 360 video frame", err);
        }
    }

    private render = () => {
        if (this.disposed) return;
        const gl = this.gl;
        this.resizeCanvas();
        this.uploadVideoFrame();

        gl.viewport(0, 0, this.canvas.width, this.canvas.height);
        const tanHalfHorizontal = Math.tan((HORIZONTAL_FOV_DEGREES * Math.PI / 180) / 2);
        const aspect = this.canvas.width / this.canvas.height;
        gl.uniform2f(this.uTanHalfFov, tanHalfHorizontal, tanHalfHorizontal / aspect);
        gl.uniform1f(this.uYaw, (this.rotation - 0.5) * 2 * Math.PI);
        gl.uniform1f(this.uPitch, (clip(this.tilt, TILT_LIMIT, 1 - TILT_LIMIT) - 0.5) * Math.PI);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

        this.animationFrame = requestAnimationFrame(this.render);
    };

    private onPointerDown = (e: PointerEvent) => {
        if (e.pointerType === "mouse" && e.button !== 0) return;
        this.dragPointerId = e.pointerId;
        this.lastDragX = this.dragStartX = e.clientX;
        this.lastDragY = this.dragStartY = e.clientY;
        this.suppressClick = false;
        this.canvas.setPointerCapture(e.pointerId);
    };

    private onPointerMove = (e: PointerEvent) => {
        if (e.pointerId !== this.dragPointerId) return;
        const deltaX = e.clientX - this.lastDragX;
        const deltaY = e.clientY - this.lastDragY;
        this.lastDragX = e.clientX;
        this.lastDragY = e.clientY;

        // Dragging "grabs" the scene: drag right to look left, drag down to look up.
        this.rotation = wrap(this.rotation - deltaX / this.canvas.clientWidth / DRAG_SENSITIVITY_X);
        this.tilt = clip(this.tilt + deltaY / this.canvas.clientHeight / DRAG_SENSITIVITY_Y);

        if (Math.abs(e.clientX - this.dragStartX) > 4 || Math.abs(e.clientY - this.dragStartY) > 4) {
            this.suppressClick = true;
        }
    };

    private onPointerUp = (e: PointerEvent) => {
        if (e.pointerId !== this.dragPointerId) return;
        this.dragPointerId = null;
        if (this.canvas.hasPointerCapture(e.pointerId)) {
            this.canvas.releasePointerCapture(e.pointerId);
        }
    };

    // A drag to look around should not also toggle play/pause.
    private onClick = (e: MouseEvent) => {
        if (this.suppressClick) {
            e.stopPropagation();
            e.preventDefault();
            this.suppressClick = false;
        }
    };
}

// Port of the WPF LookAroundInteractionHint storyboard: fades in, swings a hand left and
// right to show the view can be dragged, then fades out. Keyframe offsets are in seconds.
const HINT_DURATION_SECONDS = 6.9;
const at = (seconds: number) => seconds / HINT_DURATION_SECONDS;
const EASE_IN_OUT_CUBIC = "cubic-bezier(0.65, 0, 0.35, 1)";

function startLookAroundHint(border: HTMLElement, hand: HTMLElement) {
    stopLookAroundHint(border, hand);
    const duration = HINT_DURATION_SECONDS * 1000;

    border.animate([
        { opacity: 0, offset: 0 },
        { opacity: 0, offset: at(1) },
        { opacity: 1, offset: at(1.8) },
        { opacity: 1, offset: at(6) },
        { opacity: 0, offset: 1 },
    ], { duration, fill: "forwards" });

    hand.animate([
        { transform: "translate(0px, 0px)", offset: 0 },
        { transform: "translate(0px, 0px)", offset: at(1.8), easing: EASE_IN_OUT_CUBIC },
        { transform: "translate(-40px, 0px)", offset: at(2.8), easing: EASE_IN_OUT_CUBIC },
        { transform: "translate(0px, -10px)", offset: at(3.5), easing: EASE_IN_OUT_CUBIC },
        { transform: "translate(40px, 0px)", offset: at(4.2), easing: EASE_IN_OUT_CUBIC },
        { transform: "translate(0px, 0px)", offset: at(5.2) },
        { transform: "translate(0px, 0px)", offset: 1 },
    ], { duration, fill: "forwards" });
}

function stopLookAroundHint(border: HTMLElement, hand: HTMLElement) {
    for (const element of [border, hand]) {
        element?.getAnimations().forEach(a => a.cancel());
    }
}

(window as any).videoPlayer360 = {
    create(canvas: HTMLCanvasElement, video: HTMLVideoElement) {
        return new VideoPlayer360(canvas, video);
    },
    startLookAroundHint,
    stopLookAroundHint,
};
